import * as React from "react";
import { useEffect } from "react";
import {
  PopoverSurface,
  PopoverTrigger,
  Popover,
  makeStyles,
  Title3,
  Text,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Divider,
  Caption1,
  Button,
  DrawerBody,
  Field,
  Input,
  mergeClasses,
  Avatar,
  ProgressBar,
  Tooltip
} from "@fluentui/react-components";
import PropTypes from "prop-types";
import configs from "../utils/configs";
import bwipjs from "@bwip-js/browser";

import { Info24Filled, CopyRegular, ArrowDownloadFilled, Dismiss20Regular } from "@fluentui/react-icons";
import useBreakpoints from "./hooks/useBreakpoints";
import { ResponsiveOverlayDrawer } from "./drawer/ResponsiveOverlayDrawer";
import { hubUrl } from "../utils/phoenix-utils";
import notificationsCenter from "../singleton/NotificationsCenter";
import { useEvent } from "./room/EventContextProvider";
import { formatEventDateRange } from "../utils/events-utils";
import { FormattedMessage } from "react-intl";
import { runtimeRoomCreator } from "../utils/platform-room-api";

const useStyles = makeStyles({
  infoButtonContainer: {
    "@media (min-width: 992px)": {
      position: "fixed",
      bottom: "20px",
      left: "30px"
    }
  },
  entered: {
    right: "70px",
    "@media (min-width: 992px)": {
      right: "unset"
    }
  },
  infoButton: {
    display: "flex",
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
    border: "none",
    height: "40px",
    padding: "var(--Vertical-SNudge, 6px) var(--Horizontal-SNudge, 6px)",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: "0",
    borderRadius: "4px",
    background: "var(--toolbar-overlay-background)",
    cursor: "pointer",
    "& svg": {
      color: "var(--colorNeutralForeground1)",
      fill: "currentColor"
    }
  },
  buttonActive: {
    backgroundColor: "var(--colorNeutralBackground3)"
  },
  titleContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between"
  },
  headerContainer: {
    width: "100%",
    display: "flex",
    alignItems: "start"
  },
  titleContainerText: {
    wordBreak: "break-word"
    // display: "-webkit-box",
    // "-webkit-line-clamp": 2,
    // "-webkit-box-orient": "vertical",
    // overflow: "hidden"
  },
  scenePopover: {
    display: "flex",
    width: "300px",
    top: "-7px!important"
  },
  scenePopoverContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%"
  },
  sceneImage: {
    display: "flex",
    height: "144px",
    justifyContent: "center",
    alignItems: "center",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sceneName: {
    display: "flex",
    height: "29px",
    flexDirection: "column",
    justifyContent: "center"
  },
  sceneNameText: {
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal"
  },
  accordionHeader: {
    "& .fui-AccordionHeader__button": {
      padding: "0px"
    }
  },
  accordionHeaderText: {
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "var(--Line-height-300, 20px)"
  },
  inviteContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px"
  },
  qrCodeContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    gap: "6px"
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "var(--Font-size-100, 10px)"
  },
  accordionPanel: {
    margin: 0,
    // display: "-webkit-box",
    // "-webkit-line-clamp": 3,
    // "-webkit-box-orient": "vertical",
    // overflow: "hidden",
    marginBottom: "12px"
  },
  menuPopover: {
    top: "-15px!important"
  },
  marginTop5: {
    marginTop: "5px"
  },
  marginBottom5: {
    marginBottom: "5px"
  },
  dismissButtonContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between"
  },
  capacity: {
    color: "var(--colorNeutralForeground3)",
    fontWeight: "600"
  },
  secondaryText: {
    color: "var(--colorNeutralForeground3)"
  }
});

const SceneDetails = ({ room, onClose, dashboardChannel, isMobile = false }) => {
  const { eventData, presentationData } = useEvent();
  const resolvedDashboardChannel = dashboardChannel || window.APP?.dashboardChannel;
  const [shareUrl, setShareUrl] = React.useState(null);
  const [shareUrlLoading, setShareUrlLoading] = React.useState(
    Boolean(room?.hub_id && resolvedDashboardChannel?.shareHub)
  );
  const url = shareUrl;

  let embedText = null;
  const store = window.APP.store;
  const embedToken = room.embed_token || store.getEmbedTokenForHub(room);
  if (embedToken) {
    const embedUrl = hubUrl(room.hub_id, { embed_token: embedToken });
    embedText = `<iframe src="${embedUrl}" style="width: 1024px; height: 768px;" allow="microphone; camera; vr; speaker;"></iframe>`;
  }

  const address = (presentationData && presentationData.address) || (eventData && eventData.address);
  const fullAddress =
    address &&
    [address.location_name, address.address, address.city, address.state, address.country, address.postcode]
      .filter(Boolean)
      .join(", ");

  const roomCreator = runtimeRoomCreator(room);
  const organizer = eventData?.owner?.name || presentationData?.event?.owner?.name || roomCreator?.displayName;
  const organizerImage = eventData || presentationData ? undefined : roomCreator?.imageUrl || undefined;

  const copyUrl = url => {
    navigator.clipboard
      .writeText(url)
      .then(() => {})
      .catch(err => {
        console.error("Failed to copy: ", err);
      });
  };

  const qrCanvasRef = React.useRef(null); // Ref to the QR code canvas

  const generateQRCode = React.useCallback(() => {
    if (!qrCanvasRef.current || !url) return;

    bwipjs.toCanvas(qrCanvasRef.current, {
      bcid: "qrcode",
      text: url,
      scaleX: 2,
      scaleY: 2,
      includetext: false,
      textxalign: "center",
      paddingwidth: 3,
      paddingheight: 3,
      backgroundcolor: "#fff"
    });
  }, [url]);

  const copyQRCode = async () => {
    if (!url) return;

    try {
      const canvas = qrCanvasRef.current;
      // Convert canvas to blob
      canvas.toBlob(async blob => {
        try {
          // Create ClipboardItem with the blob
          // eslint-disable-next-line no-undef
          const clipboardItem = new ClipboardItem({ "image/png": blob });
          // Write to clipboard
          await navigator.clipboard.write([clipboardItem]);
          notificationsCenter.success("QR Code has been copied to clipboard");
        } catch (err) {
          console.error("Failed to copy QR code: ", err);
          notificationsCenter.error("Failed to copy QR Code to clipboard");
        }
      }, "image/png");
    } catch (err) {
      console.error("Failed to copy QR code: ", err);
      notificationsCenter.error("Failed to copy QR Code to clipboard");
    }
  };

  const downloadQRCode = () => {
    if (!url) return;

    // Create a temporary canvas for high-quality download
    const tempCanvas = document.createElement("canvas");
    const scale = 10; // High quality 10x scale for download

    bwipjs.toCanvas(tempCanvas, {
      bcid: "qrcode",
      text: url,
      scaleX: scale,
      scaleY: scale,
      includetext: false,
      textxalign: "center",
      paddingwidth: 3,
      paddingheight: 3,
      backgroundcolor: "#fff"
    });

    const link = document.createElement("a");
    link.href = tempCanvas.toDataURL("image/png");
    link.download = "qrcode.png";
    link.click();
  };

  const [openItems, setOpenItems] = React.useState(["1"]);
  const handleToggle = async (event, data) => {
    setOpenItems(data.openItems);
    await new Promise(resolve => setTimeout(resolve));

    if (data.openItems.includes("1") && qrCanvasRef.current) {
      generateQRCode();
    }
  };

  useEffect(() => {
    if (qrCanvasRef.current && !shareUrlLoading) {
      generateQRCode();
    }
  }, [room?.scene?.slug, generateQRCode, shareUrlLoading]);

  useEffect(() => {
    let cancelled = false;

    setShareUrl(null);

    if (!room?.hub_id || !resolvedDashboardChannel?.shareHub) {
      setShareUrlLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setShareUrlLoading(true);

    resolvedDashboardChannel
      .shareHub(room.hub_id)
      .then(url => {
        if (!cancelled) {
          setShareUrl(url || null);
          setShareUrlLoading(false);
        }
      })
      .catch(error => {
        console.error("Failed to generate room share shortlink", error);
        if (!cancelled) {
          setShareUrl(null);
          setShareUrlLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedDashboardChannel, room?.hub_id]);

  const copyMetaverseLink = () => {
    if (!url) return;

    copyUrl(url);
    notificationsCenter.success("Verse link has been copied");
  };

  const copyEmbedLink = () => {
    copyUrl(embedText);
    notificationsCenter.success("Embed code has been copied");
  };

  const styles = useStyles();

  const [additionalStyles, setAdditionalStyles] = React.useState({});
  useEffect(() => {
    const handleResize = () => {
      const containerHeight = window.innerHeight - 210;
      const contentHeight = document.querySelector("#room-description-content")?.scrollHeight;

      if (!isMobile) {
        setAdditionalStyles({
          maxHeight: `calc(100vh - 210px)`,
          overflowY: contentHeight > containerHeight ? "auto" : "hidden"
        });
      } else {
        setAdditionalStyles({});
      }
    };
    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile, room?.description]);

  return (
    <div className={styles.scenePopoverContainer} id="room-description-content">
      <div className={styles.dismissButtonContainer}>
        <div>
          {(eventData || presentationData) && (
            <Text weight="semibold" size={600}>
              <FormattedMessage id="button-info.title" defaultMessage="Information" />
            </Text>
          )}
        </div>
        <Button style={{ maxHeight: "32px" }} appearance="subtle" icon={<Dismiss20Regular />} onClick={onClose} />
      </div>

      {/* Organizer accordion */}

      <div style={{ ...additionalStyles, width: "100%", paddingLeft: "10px", paddingRight: "10px" }}>
        {organizer && (
          <>
            <Accordion collapsible multiple defaultOpenItems="organizer">
              <AccordionItem value="organizer">
                <AccordionHeader className={styles.accordionHeader}>
                  <span className={styles.accordionHeaderText}>
                    {eventData || presentationData ? (
                      <FormattedMessage id="button-info.organizer" defaultMessage="Organizer" />
                    ) : (
                      <FormattedMessage id="button-info.author" defaultMessage="Creator" />
                    )}
                  </span>
                </AccordionHeader>
                <AccordionPanel
                  className={styles.accordionPanel}
                  style={{ marginBottom: "12px", paddingLeft: "4px", paddingRight: "4px" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Avatar
                      color="brand"
                      size={32}
                      name={organizer}
                      image={organizerImage ? { src: organizerImage } : undefined}
                    />
                    <Text>{organizer}</Text>
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            <Divider />
            <div className={styles.marginBottom5}></div>
          </>
        )}

        {/* Capacity */}
        {(eventData || presentationData) && (
          <div>
            <div className={styles.capacity}>
              <FormattedMessage id="button-info.capacity" defaultMessage="Capacity" />
              :&nbsp;{presentationData?.attendees_count || eventData?.attendees_count || 0}/
              {presentationData?.capacity || eventData?.capacity || 50}
            </div>
          </div>
        )}

        <div className={styles.headerContainer}>
          <div className={styles.titleContainer}>
            <Title3 className={styles.titleContainerText}>{room?.name}</Title3>
          </div>
        </div>

        {/* Show event/presentation dates when available */}
        {(eventData || presentationData) && (
          <div className={styles.marginTop5}>
            <Text weight="bold">{formatEventDateRange(presentationData || eventData)}</Text>
          </div>
        )}

        <div style={{ width: "100%" }}>
          {room.description ||
            (room?.scene?.description && (
              <>
                <Accordion collapsible multiple defaultOpenItems="1">
                  <AccordionItem value="1">
                    <AccordionHeader className={styles.accordionHeader}>
                      <span className={styles.accordionHeaderText}>
                        <FormattedMessage id="button-info.description" defaultMessage="Description" />
                      </span>
                    </AccordionHeader>
                    <AccordionPanel
                      style={{ marginBottom: "12px", paddingLeft: "4px", paddingRight: "4px" }}
                      className={styles.accordionPanel}
                    >
                      {room?.description || room?.scene?.description}
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
                <Divider />
              </>
            ))}

          {(presentationData?.presenters?.length > 0 || (!presentationData && eventData?.presenters?.length > 0)) && (
            <>
              <Accordion collapsible multiple defaultOpenItems="speakers">
                <AccordionItem value="speakers">
                  <AccordionHeader className={styles.accordionHeader}>
                    <span className={styles.accordionHeaderText}>
                      <FormattedMessage id="button-info.speakers" defaultMessage="Speakers" />
                    </span>
                  </AccordionHeader>
                  <AccordionPanel
                    className={styles.accordionPanel}
                    style={{ marginBottom: "12px", paddingLeft: "4px", paddingRight: "4px" }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {(presentationData?.presenters || eventData?.presenters)?.map(presenter => (
                        <div key={presenter.id} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Avatar size={32} name={presenter.name || ""} color="colorful" />
                          <Text>{presenter.name}</Text>
                        </div>
                      ))}
                    </div>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
              <Divider />
            </>
          )}

          {fullAddress && (
            <>
              <Accordion style={{ width: "100%" }} collapsible multiple defaultOpenItems="1">
                <AccordionItem value="1" style={{ width: "100%" }}>
                  <AccordionHeader className={styles.accordionHeader}>
                    <span className={styles.accordionHeaderText}>
                      <FormattedMessage id="button-info.physical-address" defaultMessage="Physical Address" />
                    </span>
                  </AccordionHeader>
                  <AccordionPanel style={{ width: "100%" }} className={styles.accordionPanel}>
                    <Text>{fullAddress}</Text>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
              <Divider />
            </>
          )}

          <Accordion style={{ width: "100%" }} collapsible multiple openItems={openItems} onToggle={handleToggle}>
            <AccordionItem value="1" style={{ width: "100%" }}>
              <AccordionHeader className={styles.accordionHeader}>
                <span className={styles.accordionHeaderText}>
                  <FormattedMessage id="button-info.invite" defaultMessage="Invite" />
                </span>
              </AccordionHeader>
              <AccordionPanel style={{ width: "100%" }} className={styles.accordionPanel}>
                <div style={{ width: "100%" }} className={styles.inviteContainer}>
                  <Text size={"small"}>
                    <Caption1 className={styles.secondaryText}>
                      <FormattedMessage
                        id="button-info.scan-to-access-this-verse"
                        defaultMessage="Scan to access this Verse"
                      />
                    </Caption1>
                  </Text>

                  {shareUrlLoading ? (
                    <Field label={"Verse Link"} style={{ width: "100%" }}>
                      <ProgressBar thickness="large" />
                    </Field>
                  ) : shareUrl ? (
                    <>
                      <div className={styles.qrCodeContainer}>
                        <canvas
                          id="qr-info"
                          ref={qrCanvasRef}
                          style={{ borderRadius: "4px", border: "1px solid #BDBDBD" }}
                        ></canvas>
                        <div className={styles.buttonsContainer}>
                          <Tooltip content="Copy QR Code to clipboard">
                            <Button appearance="outline" icon={<CopyRegular />} onClick={copyQRCode} />
                          </Tooltip>
                          <Tooltip content="Download QR Code">
                            <Button appearance="primary" icon={<ArrowDownloadFilled />} onClick={downloadQRCode} />
                          </Tooltip>
                        </div>
                      </div>
                      <Field label={"Verse Link"} style={{ width: "100%" }}>
                        <Input
                          readOnly
                          value={url || ""}
                          contentAfter={<CopyRegular style={{ cursor: "pointer" }} onClick={copyMetaverseLink} />}
                          appearance="filled-darker"
                        />
                      </Field>
                    </>
                  ) : (
                    <Text size="small">Verse link is currently unavailable.</Text>
                  )}
                  {embedToken && (
                    <Field label={"Embed Code"} style={{ width: "100%" }}>
                      <Input
                        readOnly
                        value={embedText}
                        contentAfter={<CopyRegular style={{ cursor: "pointer" }} onClick={copyEmbedLink} />}
                        appearance="filled-darker"
                      />
                    </Field>
                  )}
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export const ButtonInfo = ({ room, open, onOpenChange, dashboardChannel, entered }) => {
  const styles = useStyles();
  const handleOpenChange = (e, data) => onOpenChange(data.open || false);

  const breakpoint = useBreakpoints();
  const isMobile = ["xs", "sm"].includes(breakpoint);

  return (
    <div className={mergeClasses(styles.infoButtonContainer, entered && styles.entered)}>
      {!isMobile && (
        <Popover open={open} positioning={"above-start"} withArrow onOpenChange={handleOpenChange}>
          <PopoverTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              className={!open ? styles.infoButton : mergeClasses(styles.infoButton, styles.buttonActive)}
            >
              <Info24Filled />
            </Button>
          </PopoverTrigger>

          <PopoverSurface tabIndex={-1} className={styles.scenePopover}>
            <SceneDetails room={room} dashboardChannel={dashboardChannel} onClose={() => onOpenChange(false)} />
          </PopoverSurface>
        </Popover>
      )}
      {isMobile && (
        <>
          <div className={styles.infoButton} onClick={() => onOpenChange(true)}>
            <Info24Filled />
          </div>
          <ResponsiveOverlayDrawer desktopPosition="end" size="full" open={open}>
            <DrawerBody style={{ padding: "40px 30px" }}>
              <SceneDetails
                isMobile={true}
                room={room}
                dashboardChannel={dashboardChannel}
                onClose={() => onOpenChange(false)}
              />
            </DrawerBody>
          </ResponsiveOverlayDrawer>
        </>
      )}
    </div>
  );
};

SceneDetails.propTypes = {
  isMobile: PropTypes.bool,
  room: PropTypes.object,
  dashboardChannel: PropTypes.object,
  onClose: PropTypes.func
};

ButtonInfo.propTypes = {
  room: PropTypes.object,
  scene: PropTypes.object,
  dashboardChannel: PropTypes.object,
  entered: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func
};
